'use client';

import { useCallback } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryOptions
} from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import type {
  LawFirmProfile,
  UpdateLawFirmProfileRequest,
  UpdateLawFirmUserRequest,
  InviteUsersRequest,
  UserListFilters,
  UserPermissions
} from './types';
import {
  getLawFirmProfileAction,
  updateLawFirmProfileAction,
  getLawFirmUsersAction,
  getLawFirmUserAction,
  updateLawFirmUserAction,
  inviteUsersAction,
  resendInvitationAction,
  sendInvitationAction,
  deactivateUserAction,
  reactivateUserAction,
  getLawFirmStatisticsAction,
  getUserPermissionsAction,
  getPendingInvitationsAction,
  cancelInvitationAction,
  uploadLetterheadAction
} from './actions/';
import { uploadUserSignatureAction } from '../files/actions';
import { accountKeys } from '@/lib/query-keys';
import { profileKeys } from '@/lib/query-keys/profile';

// ============================================================================
// Law Firm Profile Hooks
// ============================================================================

/**
 * Hook to fetch and manage law firm profile data
 */
export function useLawFirmProfile(options?: {
  /** Poll control — used by the trial-activation gate (LF-4076) to wait for
   *  `membershipStatus` to flip off `pending` after the Stripe round-trip. */
  refetchInterval?: UseQueryOptions<LawFirmProfile>['refetchInterval'];
  /** Skip the fetch entirely — e.g. a deactivated user, for whom the backend
   *  403s this endpoint unconditionally. This is a network-avoidance
   *  optimisation (skip a call already known to fail) rather than a
   *  correctness requirement: `gatedAction()` now carries the HTTP status
   *  onto its thrown error (VALE-954), so React Query's retry predicate
   *  correctly stops after one attempt on any 4xx even without this flag.
   *  Kept because it renders the ActivationV2Gate's lock-out screen
   *  immediately instead of after one network round trip (VALE-825).
   *  Defaults to enabled. */
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: accountKeys.lawFirmProfile,
    queryFn: () => gatedAction(() => getLawFirmProfileAction()),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateLawFirmProfileRequest) => {
      const result = await updateLawFirmProfileAction(data);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update law firm profile');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountKeys.lawFirmProfile, data);
      queryClient.invalidateQueries({ queryKey: accountKeys.lawFirmProfile });
    }
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updateProfile: updateMutation.mutate,
    updateProfileAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error
  };
}

// ============================================================================
// Law Firm Users Hooks
// ============================================================================

/**
 * Hook to fetch and manage law firm users
 */
export function useLawFirmUsers(filters?: UserListFilters) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...accountKeys.users, filters],
    queryFn: () => gatedAction(() => getLawFirmUsersAction(filters)),
    staleTime: 2 * 60 * 1000 // 2 minutes
  });

  const inviteMutation = useMutation({
    mutationFn: async (request: InviteUsersRequest) => {
      const result = await inviteUsersAction(request);
      if (!result.success) {
        throw new Error(result.error || 'Failed to invite users');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
      queryClient.invalidateQueries({
        queryKey: accountKeys.invitations
      });
    }
  });

  return {
    users: query.data?.users || [],
    totalUsers: query.data?.total || 0,
    page: query.data?.page || 1,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    inviteUsers: inviteMutation.mutate,
    inviteUsersAsync: inviteMutation.mutateAsync,
    isInviting: inviteMutation.isPending,
    inviteError: inviteMutation.error
  };
}

/**
 * Hook to manage a single law firm user
 */
export function useLawFirmUser(userId: number) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: accountKeys.user(userId),
    queryFn: () => gatedAction(() => getLawFirmUserAction(userId)),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateLawFirmUserRequest) => {
      const result = await updateLawFirmUserAction(userId, data);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update user');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountKeys.user(userId), data);
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
    }
  });

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      const result = await deactivateUserAction(userId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to deactivate user');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountKeys.user(userId), data);
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
    }
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      const result = await reactivateUserAction(userId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to reactivate user');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountKeys.user(userId), data);
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
    }
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async () => {
      const result = await resendInvitationAction(userId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to resend invitation');
      }
    }
  });

  return {
    user: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updateUser: updateMutation.mutate,
    updateUserAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deactivateUser: deactivateMutation.mutate,
    deactivateUserAsync: deactivateMutation.mutateAsync,
    isDeactivating: deactivateMutation.isPending,
    deactivateError: deactivateMutation.error,
    reactivateUser: reactivateMutation.mutate,
    reactivateUserAsync: reactivateMutation.mutateAsync,
    isReactivating: reactivateMutation.isPending,
    reactivateError: reactivateMutation.error,
    resendInvitation: resendInvitationMutation.mutate,
    resendInvitationAsync: resendInvitationMutation.mutateAsync,
    isResending: resendInvitationMutation.isPending,
    resendError: resendInvitationMutation.error
  };
}

// ============================================================================
// Statistics and Permissions Hooks
// ============================================================================

/**
 * Hook to fetch law firm statistics
 */
export function useLawFirmStatistics() {
  const query = useQuery({
    queryKey: accountKeys.statistics,
    queryFn: () => gatedAction(() => getLawFirmStatisticsAction()),
    staleTime: 10 * 60 * 1000 // 10 minutes
  });

  return {
    statistics: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch
  };
}

/**
 * Hook to fetch user permissions
 */
export function useUserPermissions(userId?: number) {
  const query = useQuery({
    queryKey: accountKeys.permissions(userId),
    queryFn: () => gatedAction(() => getUserPermissionsAction(userId)),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const hasPermission = useCallback(
    (permission: keyof UserPermissions) => {
      return query.data?.[permission] === true;
    },
    [query.data]
  );

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasPermission
  };
}

// ============================================================================
// Invitation Management Hooks
// ============================================================================

/**
 * Hook to manage pending invitations
 */
export function usePendingInvitations() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: accountKeys.invitations,
    queryFn: () => gatedAction(() => getPendingInvitationsAction()),
    staleTime: 2 * 60 * 1000 // 2 minutes
  });

  const cancelMutation = useMutation({
    mutationFn: async (userId: number) => {
      const result = await cancelInvitationAction(userId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel invitation');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountKeys.invitations
      });
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
    }
  });

  const sendMutation = useMutation({
    mutationFn: async (userId: number) => {
      const result = await sendInvitationAction(userId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to send invitation');
      }
    }
  });

  return {
    invitations: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    cancelInvitation: cancelMutation.mutate,
    cancelInvitationAsync: cancelMutation.mutateAsync,
    isCancelling: cancelMutation.isPending,
    cancelError: cancelMutation.error,
    sendInvitation: sendMutation.mutate,
    sendInvitationAsync: sendMutation.mutateAsync,
    isSending: sendMutation.isPending,
    sendError: sendMutation.error
  };
}

// ============================================================================
// File Upload Hooks
// ============================================================================

/**
 * Hook to handle letterhead upload
 */
export function useLetterheadUpload() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const result = await uploadLetterheadAction(file);
      if (!result.success) {
        throw new Error(result.error || 'Failed to upload letterhead');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(accountKeys.lawFirmProfile, data);
      queryClient.invalidateQueries({ queryKey: accountKeys.lawFirmProfile });
    }
  });

  return {
    uploadLetterhead: mutation.mutate,
    uploadLetterheadAsync: mutation.mutateAsync,
    isUploading: mutation.isPending,
    error: mutation.error,
    data: mutation.data
  };
}

// Note: Email signature upload has been removed as email signatures are now
// managed directly by the backend as HTML content instead of file uploads

/**
 * The three caches an email-signature write moves: the firm profile owns the
 * stored HTML, the profile carries the resolved render every other surface
 * reads, and the readiness report keys off the new token set. Every writer of
 * the firm signature — the settings card's save, its enabled toggle, and the
 * onboarding wizard's email-signature step — shares this one function so the
 * set can't drift between call sites the way the wizard's own copy once did
 * (VALE-1268).
 */
export function invalidateEmailSignatureCaches(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: accountKeys.lawFirmProfile });
  queryClient.invalidateQueries({ queryKey: profileKeys.user() });
  queryClient.invalidateQueries({ queryKey: accountKeys.signatureReadiness });
}

/**
 * Hook to handle personal signature (handwritten signature) upload
 */
export function usePersonalSignatureUpload(userId: number) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const result = await uploadUserSignatureAction(userId, file);
      if (!result.success) {
        throw new Error(result.error || 'Failed to upload personal signature');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      // Update the user data in the cache immediately
      queryClient.setQueryData(accountKeys.user(userId), data);
      // Then invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: accountKeys.user(userId)
      });
      // Also invalidate users list if it exists
      queryClient.invalidateQueries({ queryKey: accountKeys.users });
      // IMPORTANT: Also invalidate the profile query since it contains user data
      queryClient.invalidateQueries({ queryKey: profileKeys.user() });
      /*
        The signature prints on the sample letter, so the stored render is stale
        the moment one is uploaded (VALE-1410).

        It needs saying explicitly here because none of the keys above reaches
        it: `accountKeys.user(userId)` and `accountKeys.users` are siblings of
        the sample-letter key, not prefixes of it. The identity rows get this
        for free — `useCommitLawFirmUser` invalidates `accountKeys.all` — and
        this hook is the one writer of a value that prints on the letter which
        did not.
      */
      queryClient.invalidateQueries({
        queryKey: accountKeys.sampleLetterRoot()
      });
    }
  });

  return {
    uploadSignature: mutation.mutate,
    uploadSignatureAsync: mutation.mutateAsync,
    isUploading: mutation.isPending,
    error: mutation.error,
    data: mutation.data
  };
}
